import { Form, Row, Col } from 'react-bootstrap';

import { ANNOUNCEMENT_LIMITS } from '../../../../lib/notifications/announcements';
import { DAY_MS, toDateInput, type AnnouncementFormValues } from './announcementFormValues';

interface AnnouncementFormProps {
  values: AnnouncementFormValues;
  onChange: (values: AnnouncementFormValues) => void;
  disabled?: boolean;
}

export function AnnouncementForm({ values, onChange, disabled }: AnnouncementFormProps) {
  const set = <K extends keyof AnnouncementFormValues>(key: K, value: AnnouncementFormValues[K]): void =>
    onChange({ ...values, [key]: value });
  const now = Date.now();

  return (
    <Form onSubmit={(e) => e.preventDefault()}>
      <Form.Group className="mb-3" controlId="announcement-title">
        <Form.Label>Title</Form.Label>
        <Form.Control
          value={values.title}
          maxLength={ANNOUNCEMENT_LIMITS.title}
          onChange={(e) => set('title', e.target.value)}
          placeholder="Scheduled maintenance on Saturday"
          disabled={disabled}
        />
        <Form.Text>
          {values.title.length} of {ANNOUNCEMENT_LIMITS.title} characters.
        </Form.Text>
      </Form.Group>

      <Form.Group className="mb-3" controlId="announcement-body">
        <Form.Label>Message</Form.Label>
        <Form.Control
          as="textarea"
          rows={7}
          value={values.body}
          maxLength={ANNOUNCEMENT_LIMITS.body}
          onChange={(e) => set('body', e.target.value)}
          placeholder="What people need to know."
          disabled={disabled}
        />
        <Form.Text>
          Markdown works: **bold**, *italic*, lists and [links](https://example.com). Images are left out.{' '}
          {values.body.length} of {ANNOUNCEMENT_LIMITS.body} characters.
        </Form.Text>
      </Form.Group>

      <Row className="mb-3">
        <Col md={5}>
          <Form.Group controlId="announcement-link-label">
            <Form.Label>Button text (optional)</Form.Label>
            <Form.Control
              value={values.linkLabel}
              maxLength={ANNOUNCEMENT_LIMITS.linkLabel}
              onChange={(e) => set('linkLabel', e.target.value)}
              placeholder="Read more"
              disabled={disabled}
            />
          </Form.Group>
        </Col>
        <Col md={7}>
          <Form.Group controlId="announcement-link-url">
            <Form.Label>Button link (optional)</Form.Label>
            <Form.Control
              value={values.linkUrl}
              maxLength={ANNOUNCEMENT_LIMITS.linkUrl}
              onChange={(e) => set('linkUrl', e.target.value)}
              placeholder="https://arcanum.ai/… or /files"
              disabled={disabled}
            />
          </Form.Group>
        </Col>
        <Col xs={12}>
          <Form.Text>
            A full https:// address opens in a new tab. A Numa page such as /files opens in the workspace itself.
          </Form.Text>
        </Col>
      </Row>

      <Row className="mb-1">
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Who sees it</Form.Label>
            <Form.Check
              type="radio"
              id="announcement-audience-everyone"
              name="announcement-audience"
              label="Everyone on the workspace"
              checked={values.audience === 'everyone'}
              onChange={() => set('audience', 'everyone')}
              disabled={disabled}
            />
            <Form.Check
              type="radio"
              id="announcement-audience-admins"
              name="announcement-audience"
              label="Workspace admins only"
              checked={values.audience === 'admins'}
              onChange={() => set('audience', 'admins')}
              disabled={disabled}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3" controlId="announcement-show-until">
            <Form.Label>Show as a pop-up until</Form.Label>
            <Form.Control
              type="date"
              value={values.showUntilDate}
              min={toDateInput(now)}
              max={toDateInput(now + ANNOUNCEMENT_LIMITS.maxShowDays * DAY_MS)}
              onChange={(e) => set('showUntilDate', e.target.value)}
              disabled={disabled}
            />
            <Form.Text>
              To the end of that day, your time. A person who first opens Numa after that gets no pop-up. The
              announcement stays in their Announcements history for {ANNOUNCEMENT_LIMITS.historyDays} more days.
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>
    </Form>
  );
}
